'use client';

import React, { useMemo } from 'react';
import { Card, Badge, Alert, Button, ProgressBar } from 'react-bootstrap';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Icon from '../Icon';
import { faTrash, faRoad, faCheckCircle, faCalendarCheck, faListCheck, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { LifePartPeriodData } from './lifePartSnapshotUtils';
import { useData } from '../../storage/accessors';
import { Task } from '../../state/Task';
import { DayPlan } from '../../state/DayPlan';

interface LifePartReflectionBlockProps {
    data: LifePartPeriodData;
    dateFrom: string;
    dateTo: string;
    onRemove?: () => void;
}

export default function LifePartReflectionBlock({
    data,
    dateFrom,
    dateTo,
    onRemove
}: LifePartReflectionBlockProps) {
    const { tasks, plans, roles } = useData();

    const lifepart = roles.objects[data.uuid];

    // Все выполненные задачи этой сферы за период рефлексии
    const completedTasksCount = useMemo(() => {
        return Object.values(tasks.objects)
            .filter((t: Task) =>
                t.lifepart === data.uuid &&
                t.isDone &&
                t.completedAt
            )
            .filter((t: Task) => {
                const completedDate = format(new Date(t.completedAt!), 'yyyy-MM-dd');
                return completedDate >= dateFrom && completedDate <= dateTo;
            })
            .length;
    }, [tasks.objects, data.uuid, dateFrom, dateTo]);

    // Выполненные DayPlan этой сферы за период рефлексии
    const completedDayPlansCount = useMemo(() => {
        const taskUuids = new Set(
            Object.values(tasks.objects)
                .filter((t: Task) => t.lifepart === data.uuid)
                .map(t => t.uuid)
        );
        return Object.values(plans.objects)
            .filter((p: DayPlan) =>
                p.isDone &&
                p.date >= dateFrom &&
                p.date <= dateTo &&
                taskUuids.has(p.task)
            )
            .length;
    }, [tasks.objects, plans.objects, data.uuid, dateFrom, dateTo]);

    // Выполненные задачи roadmap за период рефлексии
    const completedRoadmapTasks = useMemo(() => {
        return Object.values(tasks.objects)
            .filter((t: Task) =>
                t.lifepart === data.uuid &&
                t.forPoint != null &&
                t.isDone &&
                t.completedAt
            )
            .filter((t: Task) => {
                const completedDate = format(new Date(t.completedAt!), 'yyyy-MM-dd');
                return completedDate >= dateFrom && completedDate <= dateTo;
            })
            .sort((a: Task, b: Task) => (a.forPoint! - b.forPoint!) || (a.stepOrder || 0) - (b.stepOrder || 0));
    }, [tasks.objects, data.uuid, dateFrom, dateTo]);

    // Проверяем, есть ли финальные данные для сферы
    const hasFinalData = data.finalScore !== null && data.finalScore !== undefined;

    const scoreDiff = data.hasInitialData && data.initialScore !== null && data.finalScore !== null
        ? data.finalScore - data.initialScore
        : 0;
    const scoreDiffColor = scoreDiff > 0 ? 'success' : scoreDiff < 0 ? 'danger' : 'secondary';
    const scoreDiffSign = scoreDiff > 0 ? '+' : '';

    // Определение текста для шапки
    const nameChanged = data.hasInitialData && data.initialName !== null && data.initialName !== data.finalName;

    // Вычисляем кружки шагов
    const stepCircles = useMemo(() => {
        const circles: { variant: 'success' | 'danger' | 'primary' | 'outline-primary'; key: number }[] = [];
        const totalSteps = data.totalSteps || 10;

        // Если есть данные за период
        if (data.hasInitialData && data.initialScore !== null && data.finalScore !== null) {
            const initialStep = data.initialScore;
            const finalStep = data.finalScore;

            for (let i = 1; i <= totalSteps; i++) {
                if (i <= initialStep && i <= finalStep) {
                    // Шаг пройден до периода и остался пройденным
                    circles.push({ variant: 'primary', key: i });
                } else if (i > initialStep && i <= finalStep) {
                    // Шаг пройден за период (зелёный)
                    circles.push({ variant: 'success', key: i });
                } else if (i <= initialStep && i > finalStep) {
                    // Шаг потерян за период (красный)
                    circles.push({ variant: 'danger', key: i });
                } else {
                    // Шаг ещё не пройден (пустой с границей primary)
                    circles.push({ variant: 'outline-primary', key: i });
                }
            }
        } else if (data.finalScore !== null) {
            // Только финальные данные (нет начальных)
            for (let i = 1; i <= totalSteps; i++) {
                if (i <= data.finalScore!) {
                    circles.push({ variant: 'primary', key: i });
                } else {
                    circles.push({ variant: 'outline-primary', key: i });
                }
            }
        } else {
            // Нет данных - все пустые
            for (let i = 1; i <= totalSteps; i++) {
                circles.push({ variant: 'outline-primary', key: i });
            }
        }

        return circles;
    }, [data.hasInitialData, data.initialScore, data.finalScore, data.totalSteps]);

    // Рендер эффективности
    const renderEfficiency = () => {
        if (data.averageEfficiency === null || data.averageEfficiency === undefined) {
            return (
                <Alert variant="light" className="mb-0 text-muted">
                    <Icon icon={faGaugeHigh} className="me-2" />
                    Нет данных об эффективности за этот период
                </Alert>
            );
        }

        const maxEfficiency = 8;
        const percentage = (data.averageEfficiency / maxEfficiency) * 100;
        
        let variant: 'success' | 'warning' | 'danger' | 'info' = 'info';
        if (data.averageEfficiency >= 6) {
            variant = 'success';
        } else if (data.averageEfficiency >= 4) {
            variant = 'warning';
        } else if (data.averageEfficiency < 4) {
            variant = 'danger';
        }

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon={faGaugeHigh} className={`text-${variant}`} />
                        <span className="text-muted">Средняя эффективность</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Badge bg={variant} className="fs-6">
                            {data.averageEfficiency.toFixed(1)} / {maxEfficiency}
                        </Badge>
                    </div>
                </div>
                <ProgressBar 
                    now={percentage} 
                    variant={variant}
                    style={{ height: '8px' }}
                />
            </div>
        );
    };

    // Если сфера удалена (нет финальных данных)
    if (!hasFinalData) {
        return (
            <Card className="mb-4 shadow-sm">
                <Card.Header
                    className="bg-transparent border-0 pb-0"
                    style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div
                                className="rounded-circle"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: data.color,
                                    opacity: 0.5
                                }}
                            />
                            <div>
                                <h5 className="mb-0">
                                    <span className="text-danger">УДАЛЕНА</span>
                                </h5>
                                {data.initialName && (
                                    <small className="text-muted">
                                        было: {data.initialName}
                                    </small>
                                )}
                            </div>
                        </div>
                        {onRemove && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={onRemove}
                            >
                                <Icon icon={faTrash} /> Удалить сферу
                            </Button>
                        )}
                    </div>
                </Card.Header>

                <Card.Body>
                    <Alert variant="light" className="mb-0 text-muted">
                        Данные для этой сферы жизни отсутствуют на конец периода
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header
                className="bg-transparent border-0 pb-0"
                style={{ borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div
                            className="rounded-circle"
                            style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: data.color
                            }}
                        />
                        <div>
                            <h5 className="mb-0">
                                {nameChanged ? (
                                    <span>
                                        <span className="text-muted">{data.initialName}</span>
                                        {' → '}
                                        <span>{data.finalName}</span>
                                    </span>
                                ) : (
                                    <span>{data.finalName}</span>
                                )}
                            </h5>

                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {scoreDiff !== 0 && (
                            <Badge bg={scoreDiffColor}>
                                {scoreDiffSign}{scoreDiff}
                            </Badge>
                        )}
                        {onRemove && (
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={onRemove}
                            >
                                <Icon icon={faTrash} /> Удалить сферу
                            </Button>
                        )}
                    </div>
                </div>
            </Card.Header>

            <Card.Body>
                {/* Визуализация шагов */}
                <div className="mb-3">
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                        {stepCircles.map((circle) => (
                            <div
                                key={circle.key}
                                className={`rounded-circle border border-2 d-flex align-items-center justify-content-center`}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: circle.variant === 'outline-primary'
                                        ? 'transparent'
                                        : `var(--bs-${circle.variant})`,
                                    borderColor: `var(--bs-${circle.variant === 'outline-primary' ? 'primary' : circle.variant})`
                                }}
                                title={`Шаг ${circle.key}`}
                            />
                        ))}
                    </div>
                    <small className="text-muted">
                        Шаги: {data.finalScore !== null ? `${data.finalScore} из ${data.totalSteps}` : 'нет данных'}
                    </small>
                </div>

                {/* Сводка по периоду */}
                <div className="d-flex flex-wrap gap-3 mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon={faListCheck} className="text-secondary" />
                        <div>
                            <small className="text-muted d-block">Выполнено задач</small>
                            <strong>{completedTasksCount}</strong>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Icon icon={faCalendarCheck} className="text-secondary" />
                        <div>
                            <small className="text-muted d-block">Выполнено планов на день</small>
                            <strong>{completedDayPlansCount}</strong>
                        </div>
                    </div>
                </div>

                {/* Средняя эффективность за период */}
                <div className="mb-3" style={{ maxWidth: '400px' }}>
                    {renderEfficiency()}
                </div>

                {/* Выполненные задачи roadmap за период */}
                <div>
                    <div className="d-flex align-items-center mb-3">
                        <Icon icon={faRoad} className="me-2 text-secondary" />
                        <h6 className="mb-0">
                            Достигнутые результаты пути
                            {completedRoadmapTasks.length > 0 && (
                                <Badge bg="secondary" className="ms-2">
                                    {completedRoadmapTasks.length}
                                </Badge>
                            )}
                        </h6>
                    </div>
                    {completedRoadmapTasks.length === 0 ? (
                        <Alert variant="light" className="mb-0 text-muted">
                            Нет выполненных задач за выбранный период
                        </Alert>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {completedRoadmapTasks.map((task) => {
                                const stepName = lifepart?.stepNames?.[String(task.forPoint)];
                                return (
                                    <div
                                        key={task.uuid}
                                        className="d-flex align-items-start gap-2 p-2 border rounded bg-body"
                                    >
                                        <Icon
                                            icon={faCheckCircle}
                                            className="text-success mt-1"
                                            style={{ fontSize: '0.9rem' }}
                                        />
                                        <div className="flex-grow-1">
                                            <span style={{ textDecoration: 'line-through', color: 'gray' }}>
                                                {task.description}
                                            </span>
                                            <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                                Шаг {task.forPoint}
                                                {stepName ? ` — ${stepName}` : ''}
                                                {task.completedAt && (
                                                    <> · {format(new Date(task.completedAt), 'd MMMM yyyy', { locale: ru })}</>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
}